import { CSSProperties, useState } from 'react';
import { Box } from '@mui/material';
import DisabledByDefaultOutlinedIcon from '@mui/icons-material/DisabledByDefaultOutlined';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import CheckCircleOutlinedIcon from '@mui/icons-material/CheckCircleOutlined';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import { SvgIconComponent } from '@mui/icons-material';
import colors from '../../../../assets/theme/base/colors';
import { DeviationsDto } from '../../../../Component/Model/Dto/EmploymentEquity/Deviations';
import { SeverityLevel } from '../../../../Component/Model/Enum/EmploymentEquity';
import { PlanSummaryMovementType } from '../../../../Component/Model/Enum/EmploymentEquity';
import { PlanSummaryTableHeader } from './PlanSummaryTableHeader';
import { PlanSummaryMovementIndividualTable } from './PlanSummaryMovementIndividualTable';
import {
    PlanSummaryTableRow,
    SubtotalVariant,
    buildTableRows,
    computeRelativeSeverity,
    formatPercentage,
    formatRelativeGap,
    formatVarianceHeadcount,
    formatVariancePercentage,
    formatPriorityRank,
} from './justificationsHelper';

interface PlanSummaryTableProps {
    occupationalLevel: DeviationsDto.PlanSummaryOccupationalLevelDto;
    scorecardId: string;
    occupationalLevelDisplayName: string;
}

const movementTypeLabels: Record<PlanSummaryMovementType, string> = {
    [PlanSummaryMovementType.Recruited]: 'Recruited',
    [PlanSummaryMovementType.PromotedIn]: 'Promoted In',
    [PlanSummaryMovementType.PromotedOut]: 'Promoted Out',
    [PlanSummaryMovementType.Terminated]: 'Terminated',
};

interface MovementModalState {
    isOpen: boolean;
    movementType?: PlanSummaryMovementType;
    raceId?: number;
    genderId?: number;
    title?: string;
}

const severityIconMap: Record<SeverityLevel, { Icon: SvgIconComponent; bg: string; iconColor: string }> = {
    [SeverityLevel.Critical]:        { Icon: DisabledByDefaultOutlinedIcon, bg: colors.deviationSeverity.critical.bg,        iconColor: colors.deviationSeverity.critical.icon },
    [SeverityLevel.High]:            { Icon: WarningAmberOutlinedIcon,      bg: colors.deviationSeverity.high.bg,            iconColor: colors.deviationSeverity.high.icon },
    [SeverityLevel.Medium]:          { Icon: InfoOutlinedIcon,              bg: colors.deviationSeverity.medium.bg,          iconColor: colors.deviationSeverity.medium.icon },
    [SeverityLevel.OnTrack]:         { Icon: CheckCircleOutlinedIcon,       bg: colors.deviationSeverity.onTrack.bg,         iconColor: colors.deviationSeverity.onTrack.icon },
    [SeverityLevel.OverRepresented]: { Icon: TrendingUpIcon,                bg: colors.deviationSeverity.overRepresented.bg, iconColor: colors.deviationSeverity.overRepresented.icon },
};

const rowBorder: string = `1px solid ${colors.grey[200]}`;
const groupBorder: string = `1px solid ${colors.ui.border}`;
const subtotalDarkBorder: string = `1px solid ${colors.ui.subtotalRowBorder}`;
const subtotalDarkBorderThick: string = `2px solid ${colors.ui.subtotalRowBorder}`;

const tableStyle: CSSProperties = {
    width: '100%',
    minWidth: 1340,
    borderCollapse: 'collapse',
    backgroundColor: colors.white.main,
    tableLayout: 'fixed',
};

const baseCell: CSSProperties = {
    padding: '8px 6px',
    fontSize: '0.8125rem',
    textAlign: 'center',
    color: colors.dark.main,
    borderTop: rowBorder,
    whiteSpace: 'nowrap',
};

const tintedCell: CSSProperties = {
    ...baseCell,
    backgroundColor: colors.ui.tintedColumnBg,
};

const groupCell: CSSProperties = {
    ...baseCell,
    textAlign: 'left',
    paddingLeft: 16,
    fontWeight: 500,
    borderRight: groupBorder,
};

const cellGroupBoundary: CSSProperties = {
    ...baseCell,
    borderRight: groupBorder,
};

const tintedCellGroupBoundary: CSSProperties = {
    ...tintedCell,
    borderRight: groupBorder,
};

interface SubtotalStyles {
    rowBackground: string;
    fontWeight: number;
    fontStyle: 'normal' | 'italic';
    borderTop: string;
    borderBottom: string;
    applyVarianceColours: boolean;
}

const subtotalVariantStyles: Record<SubtotalVariant, SubtotalStyles> = {
    designatedMale:   { rowBackground: colors.ui.subtotalRowBackground, fontWeight: 700, fontStyle: 'normal', borderTop: subtotalDarkBorder,      borderBottom: subtotalDarkBorder,      applyVarianceColours: true  },
    designatedFemale: { rowBackground: colors.ui.subtotalRowBackground, fontWeight: 700, fontStyle: 'normal', borderTop: subtotalDarkBorder,      borderBottom: subtotalDarkBorder,      applyVarianceColours: true  },
    totalDesignated:  { rowBackground: colors.ui.subtotalRowEmphasisBg, fontWeight: 700, fontStyle: 'normal', borderTop: subtotalDarkBorderThick, borderBottom: subtotalDarkBorderThick, applyVarianceColours: true  },
    nonDesignated:    { rowBackground: colors.ui.subtotalRowBackground, fontWeight: 400, fontStyle: 'italic', borderTop: subtotalDarkBorder,      borderBottom: subtotalDarkBorder,      applyVarianceColours: false },
};

function varianceTone(value: number | null | undefined): 'negative' | 'neutral' | 'positive' | null {
    if (value === null || value === undefined) return null;
    if (value < 0) return 'negative';
    if (value > 0) return 'positive';
    return 'neutral';
}

function varianceCellStyle(value: number | null | undefined, base: CSSProperties): CSSProperties {
    const tone = varianceTone(value);
    if (tone === null) return { ...base, color: colors.ui.mutedText };
    const palette = colors.deviationVariance[tone];
    return {
        ...base,
        backgroundColor: palette.bg,
        color: palette.cellText,
        fontWeight: 700,
    };
}

function SeverityIcon({ relativeGapPercentage }: { relativeGapPercentage: number | null }) {
    const severity: SeverityLevel = computeRelativeSeverity(relativeGapPercentage);
    const { Icon, bg, iconColor } = severityIconMap[severity];
    return (
        <Box
            component="span"
            sx={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 20,
                height: 20,
                borderRadius: '4px',
                backgroundColor: bg,
                mr: 1,
                verticalAlign: 'middle',
                flexShrink: 0,
            }}
        >
            <Icon sx={{ width: 14, height: 14, color: iconColor }} />
        </Box>
    );
}

const movementLinkStyle: CSSProperties = {
    background: 'none',
    border: 'none',
    padding: 0,
    font: 'inherit',
    color: colors.primary.main,
    textDecoration: 'underline',
    cursor: 'pointer',
};

function renderMovementCell(value: number, cellStyle: CSSProperties, onClick: () => void) {
    if (value > 0) {
        return (
            <td style={cellStyle}>
                <button type="button" style={movementLinkStyle} onClick={onClick}>{value}</button>
            </td>
        );
    }
    return <td style={cellStyle}>{value}</td>;
}

function renderDemographicRow(
    row: Extract<PlanSummaryTableRow, { kind: 'demographic' }>,
    onMovementClick: (movementType: PlanSummaryMovementType, data: DeviationsDto.PlanSummaryRowDto, label: string) => void
) {
    const { label, data } = row;
    return (
        <tr key={`demo-${data.raceId}-${data.genderId}`}>
            <td style={groupCell}>
                <SeverityIcon relativeGapPercentage={data.relativeGapPercentage} />
                {label}
            </td>
            <td style={tintedCell}>{data.startHeadcount}</td>
            <td style={tintedCellGroupBoundary}>{formatPercentage(data.startPercentage)}</td>
            {renderMovementCell(data.recruited, baseCell, () => onMovementClick(PlanSummaryMovementType.Recruited, data, label))}
            {renderMovementCell(data.promotedIn, baseCell, () => onMovementClick(PlanSummaryMovementType.PromotedIn, data, label))}
            {renderMovementCell(data.promotedOut, baseCell, () => onMovementClick(PlanSummaryMovementType.PromotedOut, data, label))}
            {renderMovementCell(data.terminated, cellGroupBoundary, () => onMovementClick(PlanSummaryMovementType.Terminated, data, label))}
            <td style={tintedCell}>{data.currentHeadcount}</td>
            <td style={tintedCellGroupBoundary}>{formatPercentage(data.currentPercentage)}</td>
            <td style={baseCell}>{data.targetHeadcount}</td>
            <td style={cellGroupBoundary}>{formatPercentage(data.targetPercentage)}</td>
            <td style={varianceCellStyle(data.varianceHeadcount, baseCell)}>{formatVarianceHeadcount(data.varianceHeadcount)}</td>
            <td style={{ ...varianceCellStyle(data.variancePercentage, baseCell), borderRight: groupBorder }}>{formatVariancePercentage(data.variancePercentage)}</td>
            <td style={varianceCellStyle(data.relativeGapPercentage, baseCell)}>{formatRelativeGap(data.relativeGapPercentage)}</td>
            <td style={{ ...baseCell, fontWeight: 700 }}>{formatPriorityRank(data.priorityRank)}</td>
        </tr>
    );
}

function renderSubtotalRow(row: Extract<PlanSummaryTableRow, { kind: 'subtotal' }>) {
    const { variant, label, data } = row;
    const variantStyles = subtotalVariantStyles[variant];

    const rowStyle: CSSProperties = {
        backgroundColor: variantStyles.rowBackground,
    };

    const cellBase: CSSProperties = {
        ...baseCell,
        backgroundColor: variantStyles.rowBackground,
        fontWeight: variantStyles.fontWeight,
        fontStyle: variantStyles.fontStyle,
        borderTop: variantStyles.borderTop,
        borderBottom: variantStyles.borderBottom,
    };

    const cellBoundary: CSSProperties = {
        ...cellBase,
        borderRight: groupBorder,
    };

    const groupLabelCell: CSSProperties = {
        ...cellBase,
        textAlign: 'left',
        paddingLeft: 16,
        borderRight: groupBorder,
    };

    const rankCell: CSSProperties = {
        ...cellBase,
        fontWeight: 400,
        color: colors.ui.mutedText,
    };

    const varianceCell = (value: number | null | undefined, withRightBorder: boolean): CSSProperties => {
        if (!variantStyles.applyVarianceColours) {
            return withRightBorder ? { ...cellBase, borderRight: groupBorder } : cellBase;
        }
        const styled = varianceCellStyle(value, cellBase);
        return withRightBorder ? { ...styled, borderRight: groupBorder } : styled;
    };

    return (
        <tr key={`sub-${label}`} style={rowStyle}>
            <td style={groupLabelCell}>{label}</td>
            <td style={cellBase}>{data.startHeadcount}</td>
            <td style={cellBoundary}>{formatPercentage(data.startPercentage)}</td>
            <td style={cellBase}>{data.recruited}</td>
            <td style={cellBase}>{data.promotedIn}</td>
            <td style={cellBase}>{data.promotedOut}</td>
            <td style={cellBoundary}>{data.terminated}</td>
            <td style={cellBase}>{data.currentHeadcount}</td>
            <td style={cellBoundary}>{formatPercentage(data.currentPercentage)}</td>
            <td style={cellBase}>{data.targetHeadcount}</td>
            <td style={cellBoundary}>{formatPercentage(data.targetPercentage)}</td>
            <td style={varianceCell(data.varianceHeadcount, false)}>{formatVarianceHeadcount(data.varianceHeadcount)}</td>
            <td style={varianceCell(data.variancePercentage, true)}>{formatVariancePercentage(data.variancePercentage)}</td>
            <td style={rankCell}>—</td>
            <td style={rankCell}>—</td>
        </tr>
    );
}

export function PlanSummaryTable({ occupationalLevel, scorecardId, occupationalLevelDisplayName }: PlanSummaryTableProps) {
    const rows: PlanSummaryTableRow[] = buildTableRows(occupationalLevel);
    const [movementModalState, setMovementModalState] = useState<MovementModalState>({ isOpen: false });

    const handleMovementClick = (movementType: PlanSummaryMovementType, data: DeviationsDto.PlanSummaryRowDto, label: string) => {
        setMovementModalState({
            isOpen: true,
            movementType,
            raceId: data.raceId,
            genderId: data.genderId,
            title: `${occupationalLevelDisplayName} - ${movementTypeLabels[movementType]} - ${label}`,
        });
    };

    const closeMovementModal = () => {
        setMovementModalState({ isOpen: false });
    };

    return (
        <Box sx={{ borderRadius: '8px', border: `1px solid ${colors.ui.border}`, overflowX: 'auto' }}>
            <table style={tableStyle}>
                <colgroup>
                    <col />
                    <col style={{ width: '80px' }} />
                    <col style={{ width: '80px' }} />
                    <col style={{ width: '80px' }} />
                    <col style={{ width: '80px' }} />
                    <col style={{ width: '80px' }} />
                    <col style={{ width: '80px' }} />
                    <col style={{ width: '80px' }} />
                    <col style={{ width: '80px' }} />
                    <col style={{ width: '80px' }} />
                    <col style={{ width: '80px' }} />
                    <col style={{ width: '80px' }} />
                    <col style={{ width: '80px' }} />
                    <col style={{ width: '96px' }} />
                    <col style={{ width: '80px' }} />
                </colgroup>
                <PlanSummaryTableHeader />
                <tbody>
                    {rows.map(row => row.kind === 'demographic' ? renderDemographicRow(row, handleMovementClick) : renderSubtotalRow(row))}
                </tbody>
            </table>
            {movementModalState.isOpen && movementModalState.movementType !== undefined && (
                <PlanSummaryMovementIndividualTable
                    showModal={movementModalState.isOpen}
                    handleCancel={closeMovementModal}
                    title={movementModalState.title!}
                    scorecardId={scorecardId}
                    occupationalLevelId={occupationalLevel.occupationalLevelId}
                    raceId={movementModalState.raceId!}
                    genderId={movementModalState.genderId!}
                    movementType={movementModalState.movementType}
                />
            )}
        </Box>
    );
}
